import { useState, useCallback, memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { validatePassword, validateField } from '../../../util/validation';

const EditablePassword = memo(function EditablePassword({
  value,
  setValue,
  isEditable = true,
  label = null,
  isDialog = false,
  required = false,
  showValidation = false,
  schema = null,
}) {
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordErrors, setPasswordErrors] = useState([]);

  const handlePasswordDialogOpen = useCallback(e => {
    e.stopPropagation();
    setPasswordDialogOpen(true);
    setPassword('');
    setConfirmPassword('');
    setPasswordErrors([]);
  }, []);

  const handlePasswordDialogClose = useCallback(() => {
    setPasswordDialogOpen(false);
    setPassword('');
    setConfirmPassword('');
    setPasswordErrors([]);
  }, []);

  const handlePasswordSave = useCallback(() => {
    if (required && schema) {
      const fieldValidation = validateField(password, schema);
      if (!fieldValidation.isValid) {
        setPasswordErrors(fieldValidation.errors);
        return;
      }
    }

    const validation = validatePassword(password, confirmPassword);
    if (validation.isValid) {
      setValue(password);
      handlePasswordDialogClose();
    } else {
      setPasswordErrors(validation.errors);
    }
  }, [password, confirmPassword, setValue, handlePasswordDialogClose, required, schema]);

  if (isDialog) {
    return (
      <TextField
        fullWidth
        label={label}
        type='password'
        value={value || ''}
        onChange={e => setValue(e.target.value)}
        required={required}
        error={showValidation && required && !value}
        helperText={showValidation && required && !value ? 'Passwort ist erforderlich' : ''}
      />
    );
  }

  return (
    <>
      <Box
        onClick={handlePasswordDialogOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          cursor: isEditable ? 'pointer' : 'default',
          p: 1,
          borderRadius: 1,
          '&:hover': isEditable
            ? {
                bgcolor: 'action.hover',
                '& .edit-icon': {
                  visibility: 'visible',
                  opacity: 1,
                },
              }
            : {},
          position: 'relative',
          width: '100%',
          minHeight: '40px',
        }}
      >
        <Typography sx={{ flex: 1 }}>••••••••</Typography>
        {isEditable && (
          <EditIcon
            className='edit-icon'
            sx={{
              fontSize: 'small',
              ml: 1,
              color: 'action.active',
              visibility: 'hidden',
              opacity: 0,
              transition: 'all 0.2s ease-in-out',
            }}
          />
        )}
      </Box>

      <Dialog
        open={passwordDialogOpen}
        onClose={handlePasswordDialogClose}
        onClick={e => e.stopPropagation()}
      >
        <DialogTitle>Passwort ändern</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin='dense'
            label='Neues Passwort'
            type='password'
            fullWidth
            value={password}
            onChange={e => setPassword(e.target.value)}
            error={passwordErrors.length > 0}
            required={required}
          />
          <TextField
            margin='dense'
            label='Passwort bestätigen'
            type='password'
            fullWidth
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            error={passwordErrors.length > 0}
            helperText={passwordErrors.join(', ')}
            required={required}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handlePasswordDialogClose}>Abbrechen</Button>
          <Button
            onClick={handlePasswordSave}
            variant='contained'
            color='primary'
            disabled={!password || !confirmPassword}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default EditablePassword;
